import Container from "@components/Atomic/Container/Container";
import { Icon } from "@components/Icon";
import { Image } from "@components/Image/Image";
import { Link } from "@components/Link/Link";
import { StyledButton } from "@components/Styled/Button";
import { StyledContainer } from "@components/Styled/Container";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { StyledH5 } from "@components/Styled/Text";
import React, { lazy, Suspense } from "react";
import SwiperCore, { Autoplay } from "swiper";
import { SwiperSlide } from "swiper/react";
import tw from "twin.macro";
import { withTestimonialSlider } from "./withTestimonialSlider";

const Swiper = lazy(
  () => import(/* webpackChunkName: "swiper" */ "../../Swiper/Swiper"),
);
SwiperCore.use([Autoplay]);

const Wrapper = tw(StyledContainer)`
  relative pb-maxi sm:px-mega md:px-tera
`;
const SlideWrapper = tw.div`
  text-center px-midi py-maxi md:py-tera md:px-mega
`;
const ContentWrapper = tw.span`
  block pt-mini font-display text-body leading-caption md:px-mega md:text-h3
`;
const AuthorWrapper = tw.div`
  pt-mini md:text-center
`;
const ImageWrapper = tw.div`
  inline-block align-top rounded-full overflow-hidden w-giga
`;
const AuthorInfo = tw.div`
  inline-block ml-mini
`;
const AuthorName = tw(StyledH5)`
  text-left uppercase
`;
const StyledImage = tw(Image)`
  max-w-full
`;
const LearnMore = tw.div`
  underline mt-mini
`;
const Button = tw(StyledButton)`
  flex items-start justify-between text-left
`;
const StyledArrow = styledWithInvalidProps("div")(
  ({ direction }: { direction: string }) => [
    tw`absolute hidden cursor-pointer top-1/2 text-primary hover:opacity-70 md:block`,
    direction === "prev" ? tw`left-0 transform rotate-180` : tw`right-0`,
  ],
);

const SliderArrow = ({ id, direction }: { id: string; direction: string }) => (
  <StyledArrow id={id} direction={direction}>
    <Icon name="vintageArrow" size="xsmall" />
  </StyledArrow>
);

const TestimonialSlider = withTestimonialSlider(
  ({ sectionLinks, sliderAutoDelay, styles }) => {
    return (
      <Container
        styles={styles}
        data-component-type="section-testimonial-slider"
      >
        <Wrapper width="xl">
          <SliderArrow id="prev" direction="prev" />
          <SliderArrow id="next" direction="next" />
          <Suspense fallback={<></>}>
            <Swiper
              autoplay={sliderAutoDelay}
              loop={true}
              navigation={{
                prevEl: "#prev",
                nextEl: "#next",
              }}
              observeParents={true}
              observer={true}
              pagination={{ clickable: true }}
              slidesPerView={1}
              spaceBetween={10}
            >
              {sectionLinks?.map((section) => (
                <SwiperSlide key={section.title}>
                  <SlideWrapper>
                    <StyledH5>{section.title}</StyledH5>
                    <ContentWrapper>{section.content}</ContentWrapper>
                    <AuthorWrapper>
                      <ImageWrapper>
                        <StyledImage image={section.image} />
                      </ImageWrapper>
                      <AuthorInfo>
                        <AuthorName>{section.author}</AuthorName>
                        <Button
                          as={Link}
                          to={section.sectionLink.url}
                          {...section}
                        >
                          <LearnMore>{section.sectionLink.title}</LearnMore>
                        </Button>
                      </AuthorInfo>
                    </AuthorWrapper>
                  </SlideWrapper>
                </SwiperSlide>
              ))}
            </Swiper>
          </Suspense>
        </Wrapper>
      </Container>
    );
  },
);

export default TestimonialSlider;
